import { AppErrorCard } from '@components/error-card/app-error-card';
import { ScreenReaderElement } from '@components/screen-reader/screen-reader-element';
import { InfoMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import type { RequestException } from '@eika-infrastruktur/request';
import { useAccountForChildProcessRules } from '@react-query/hooks/query/use-account-for-child-process-rules';
import { format } from '@translation-utils/use-translation';
import { useTranslation } from '@translations';
import Skeleton from 'react-loading-skeleton';
import './governor-amount-threshold-details.scss';
import { formatNok } from '@utils/text-utils';

export const GovernorAmountThresholdDetails = () => {
    const { governorAmountThresholdDetails, tip } = useTranslation();
    const { data: childProcessRules, isPending, error, refetch } = useAccountForChildProcessRules();

    if (isPending) {
        return <GovernorAmountThresholdDetailsSkeleton />;
    }

    if (error) {
        return <GovernorAmountThresholdDetailsError error={error} onRetry={() => refetch()} />;
    }

    const { governorAmountThreshold } = childProcessRules;

    return (
        <div className="governor-amount-threshold-details" data-testid="governor-amount-threshold-details">
            <div>
                <h2 className="typography-subtitle-bold">{governorAmountThresholdDetails.over2gHeader}</h2>
                <p>
                    {format(
                        governorAmountThresholdDetails.over2gIngress,
                        formatNok(governorAmountThreshold.value),
                        governorAmountThreshold.year,
                    )}
                </p>
            </div>
            <div>
                <h2 className="typography-subtitle-bold">{governorAmountThresholdDetails.uncommonHeader}</h2>
                <p>{governorAmountThresholdDetails.uncommonIngress}</p>
            </div>

            <InfoMessage header={tip} text={governorAmountThresholdDetails.tip} icon={IconTypes.LYSPAERE_24} />
        </div>
    );
};

const GovernorAmountThresholdDetailsSkeleton = () => (
    <div className="governor-amount-threshold-details" data-testid="governor-amount-threshold-details-skeleton">
        <Skeleton height={32} width="50%" />
        <Skeleton count={3} />
    </div>
);

interface GovernorAmountThresholdDetailsErrorProps {
    error: RequestException;
    onRetry: () => void;
}

const GovernorAmountThresholdDetailsError = ({ error, onRetry }: GovernorAmountThresholdDetailsErrorProps) => {
    const { governorAmountThresholdDetails, tryAgain } = useTranslation();

    return (
        <AppErrorCard
            description={governorAmountThresholdDetails.error}
            exception={error}
            data-testid="governor-amount-threshold-details-error"
        >
            <ScreenReaderElement text={governorAmountThresholdDetails.error} role="alert" />
            <StandardButton onClick={onRetry}>{tryAgain}</StandardButton>
        </AppErrorCard>
    );
};
